import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Table from '../Listing/Table';
import Select from '../Form/Fields/Select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTriangleExclamation, faCircleCheck,
  faCirclePlus, faCircleMinus, faHistory, faPencil, faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { formatExpirationDate } from '../../utils/FormatDate';
import { API_BASE_URL } from '../../api';
import StockBatchMovementModal from '../Modal/StockBatchMovementModal';
import StockEditModal from '../Modal/StockEditModal';
import EditBatchModal from '../Modal/EditBatchModal';
import { useAuth } from '../../context/AuthProvider';

const isExpiredBatch = (batch) => {
  if (!batch.expirationDate) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(batch.expirationDate) < today;
};

const EXPIRY_STATUS_OPTIONS = [
  { value: 'valide', label: 'Lots valides' },
  { value: 'perime', label: 'Lots périmés' },
  { value: '', label: 'Tous les lots' },
];

const ProductStock = ({ product, onBatchUpdate, onCountChange }) => {
  const { hasRight } = useAuth();
  const canEdit = hasRight('PRODUCTS_EDIT');
  const [batches, setBatches] = useState([]);
  const [stockBatchMovementModal, setStockBatchMovementModal] = useState(false);
  const [selectedBatchId, setSelectedBatchId] = useState(null);
  const [movementType, setMovementType] = useState(null);
  const [createMovementModal, setCreateMovementModal] = useState(false);
  const [editBatchModal, setEditBatchModal] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);

  const fetchBatches = useCallback(() => {
    axios.get(`${API_BASE_URL}/api/v1/stock/orderable/${product.id}/batches`, { withCredentials: true })
      .then(({ data }) => {
        const content = data.content || [];
        setBatches(content);
        onCountChange?.(content.length);
      })
      .catch(() => setBatches([]));
  }, [product.id, onCountChange]);

  useEffect(() => { fetchBatches(); }, [fetchBatches]);

  const handleCreateMovement = (batchId, type) => {
    setSelectedBatchId(batchId);
    setMovementType(type);
    setCreateMovementModal(true);
  };

  const handleViderLot = (batch) => {
    const qty = Math.max(0, batch.quantity);
    if (qty === 0) return;
    if (!window.confirm(`Vider ce lot (${qty} unité${qty > 1 ? 's' : ''}) ? Cette action est irréversible.`)) return;
    axios.post(`${API_BASE_URL}/api/v1/stock/remove`, null, {
      withCredentials: true,
      params: { batchId: batch.id, quantity: qty, description: 'Retrait lot périmé' },
    })
      .then(() => { fetchBatches(); onBatchUpdate?.(); })
      .catch(err => toast.error(err?.response?.data?.message ?? 'Erreur lors du vidage du lot'));
  };

  // Carries the expiry status as plain text so the classic Filtres column
  // filter (a substring match under the hood) can target it — not rendered
  // as a visible column, just a filter source.
  const rows = batches.map(b => ({ ...b, expiryStatus: isExpiredBatch(b) ? 'perime' : 'valide' }));

  const columns = [
    {
      Header: "#",
      accessor: "id",
      Cell: (row) => <span style={{ fontSize: 14, fontWeight: 500, color: "#9ca3af" }}>{row.id}</span>,
    },
    {
      Header: "Expiration",
      accessor: "expirationDate",
      className: "text-center",
      Cell: (row) => {
        if (!row.expirationDate) return <span className="cell-muted">—</span>;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const diffDays = Math.ceil((new Date(row.expirationDate) - today) / (1000 * 60 * 60 * 24));
        const isExpired = diffDays < 0;
        const isSoon = diffDays >= 0 && diffDays <= 90;
        const color = isExpired ? "#dc2626" : isSoon ? "#d97706" : "#059669";
        return (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color, fontWeight: 600, fontSize: 13 }}>
            <FontAwesomeIcon icon={isExpired || isSoon ? faTriangleExclamation : faCircleCheck} style={{ fontSize: 11 }} />
            {formatExpirationDate(row.expirationDate)}
          </span>
        );
      },
    },
    {
      Header: "Quantité",
      accessor: "quantity",
      className: "text-center",
      Cell: (row) => (
        <span className={`fw-bold ${row.quantity > 0 ? 'text-success' : row.quantity < 0 ? 'text-danger' : 'text-muted'}`}>
          {row.quantity}
        </span>
      ),
    },
    {
      Header: "N° de lot",
      accessor: "batchNumber",
      Cell: (row) => <span className="fw-bold">{row.batchNumber || '—'}</span>,
    },
    {
      Header: "",
      accessor: "actions",
      className: "col-actions",
      Cell: (row) => {
        const isExpiredWithStock = isExpiredBatch(row) && row.quantity > 0;
        return (
          <div style={{ display: 'flex', gap: 6 }}>
            {canEdit && (
              <button type="button" className="modal-icon-btn-success" onClick={() => handleCreateMovement(row.id, 'INBOUND')} data-tooltip-id="tooltip" data-tooltip-content="Ajouter">
                <FontAwesomeIcon icon={faCirclePlus} />
              </button>
            )}
            {canEdit && (
              <button type="button" className="modal-icon-btn-danger" onClick={() => handleCreateMovement(row.id, 'OUTBOUND')} data-tooltip-id="tooltip" data-tooltip-content="Retirer" disabled={Math.max(0, row.quantity) === 0}>
                <FontAwesomeIcon icon={faCircleMinus} />
              </button>
            )}
            {canEdit && (
              <button type="button" className="modal-icon-btn-neutral" onClick={() => { setSelectedBatch(row); setEditBatchModal(true); }} data-tooltip-id="tooltip" data-tooltip-content="Modifier">
                <FontAwesomeIcon icon={faPencil} />
              </button>
            )}
            <button type="button" className="modal-icon-btn-neutral" onClick={() => { setSelectedBatchId(row.id); setStockBatchMovementModal(true); }} data-tooltip-id="tooltip" data-tooltip-content="Historique">
              <FontAwesomeIcon icon={faHistory} />
            </button>
            {canEdit && isExpiredWithStock && (
              <button type="button" className="modal-icon-btn-danger" onClick={() => handleViderLot(row)} data-tooltip-id="tooltip" data-tooltip-content="Vider le lot périmé">
                <FontAwesomeIcon icon={faTrash} />
              </button>
            )}
          </div>
        );
      },
    },
    {
      Header: "",
      accessor: "expiryStatus",
      visible: false,
      filterable: { component: Select, options: EXPIRY_STATUS_OPTIONS, label: 'Statut du lot' },
    },
  ];

  return (
    <>
      <Table
        mode="client"
        rowId="id"
        columns={columns}
        data={rows}
        pageSize={10}
        emptyMessage="Aucun lot en stock"
        filters={{ expiryStatus: 'valide' }}
      />

      <StockBatchMovementModal
        batchId={selectedBatchId}
        productName={product.name}
        batch={batches.find(b => b.id === selectedBatchId)}
        show={stockBatchMovementModal}
        onClose={() => setStockBatchMovementModal(false)}
      />

      <StockEditModal
        batchId={selectedBatchId}
        action={movementType}
        show={createMovementModal}
        onClose={() => setCreateMovementModal(false)}
        onSuccess={() => { fetchBatches(); onBatchUpdate?.(); }}
      />

      <EditBatchModal
        batch={selectedBatch}
        show={editBatchModal}
        onClose={() => setEditBatchModal(false)}
        onSuccess={() => { fetchBatches(); onBatchUpdate?.(); }}
      />
    </>
  );
};

export default ProductStock;
